import React, { useEffect, useState } from 'react';
import {
  Content,
  PageWithHeader,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { Link } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Chip,
} from '@material-ui/core';
import { getEntityRoute, normalizeOwnerRef } from '../catalog/catalogDiscovery';

const getBorderColor = (theme: Theme) =>
  (theme.palette as any).border || theme.palette.divider;

const getNavigationBackground = (theme: Theme) =>
  (theme.palette as any).navigation?.background ||
  theme.palette.background.paper;

const getHeaderTitleColor = (theme: Theme) =>
  theme.palette.info?.light || theme.palette.primary.main;

const getLinkColor = (theme: Theme) =>
  (theme.palette as any).link || theme.palette.primary.main;

const getLinkHoverColor = (theme: Theme) =>
  (theme.palette as any).linkHover || theme.palette.primary.dark;

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    overflowX: 'hidden',
    width: '100%',
    '& header': {
      backgroundColor: `${getNavigationBackground(theme)} !important`,
      backgroundImage: 'none !important',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: `${getHeaderTitleColor(theme)} !important`,
    },
  },
  content: {
    maxWidth: '1400px !important',
    width: '100% !important',
    padding: `${theme.spacing(3)}px !important`,
    margin: '0 auto !important',
    '& > *': {
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    },
    '& .MuiPaper-root': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    '& .MuiTableHead-root': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '& .MuiTableCell-head': {
      color: `${theme.palette.text.primary} !important`,
      fontWeight: 600,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },
    '& .MuiTableCell-body': {
      color: `${theme.palette.text.primary} !important`,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      maxWidth: '400px',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '& a': {
      color: `${getLinkColor(theme)} !important`,
      textDecoration: 'none',
      fontWeight: 600,
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      display: 'inline-block',
      maxWidth: '100%',
      '&:hover': {
        color: `${getLinkHoverColor(theme)} !important`,
        textDecoration: 'underline',
      },
    },
    '& .MuiTable-root': {
      tableLayout: 'auto',
      width: '100%',
      minWidth: 'auto',
    },
    '& .MuiTableContainer-root': {
      overflowX: 'auto',
      maxWidth: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: `${theme.spacing(2)}px !important`,
    },
  },
  filterPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${getBorderColor(theme)}`,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
  },
  filterTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: '1rem',
  },
  filterControl: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      minHeight: 56,
      '& fieldset': {
        borderColor: getBorderColor(theme),
      },
      '&:hover fieldset': {
        borderColor: theme.palette.secondary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiSelect-select, & .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      minHeight: 56,
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
  tagChip: {
    margin: theme.spacing(0.25),
    height: 24,
  },
}));

const formatKind = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const LibrariesPage = () => {
  const classes = useStyles();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [lifecycleFilter, setLifecycleFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [nameFilter, setNameFilter] = useState<string>('');

  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const [availableLifecycles, setAvailableLifecycles] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const config = useApi(configApiRef);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const backendUrl = config.getString('backend.baseUrl');
        const response = await fetch(`${backendUrl}/api/catalog/entities`);
        const data = await response.json();
        const fetchedEntities = data.items || [];

        // Filter only libraries
        const libraries = fetchedEntities.filter((entity: Entity) => {
          const type = (entity as any).spec?.type;
          return type && type.toLowerCase() === 'library';
        });

        setAllEntities(libraries);

        const owners = new Set<string>();
        const lifecycles = new Set<string>();
        const tags = new Set<string>();

        libraries.forEach((entity: Entity) => {
          const owner = (entity as any).spec?.owner;
          if (owner) {
            const ownerStr = typeof owner === 'string' ? owner : String(owner);
            owners.add(ownerStr);
          }

          const lifecycle = (entity as any).spec?.lifecycle;
          if (lifecycle) {
            const lifecycleStr =
              typeof lifecycle === 'string' ? lifecycle : String(lifecycle);
            lifecycles.add(lifecycleStr.toLowerCase());
          }

          // Extract all tags from the entity
          const entityTags = entity.metadata.tags || [];
          entityTags.forEach(tag => {
            tags.add(tag.toLowerCase());
          });
        });

        setAvailableOwners(['all', ...Array.from(owners).sort()]);
        setAvailableLifecycles(['all', ...Array.from(lifecycles).sort()]);
        setAvailableTags(['all', ...Array.from(tags).sort()]);
      } catch (error) {
        console.error('Error fetching entities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEntities();
  }, [config]);

  useEffect(() => {
    let filtered = allEntities;

    if (nameFilter.trim() !== '') {
      const searchTerm = nameFilter.toLowerCase();
      filtered = filtered.filter((entity: Entity) => {
        const name = entity.metadata.name;
        return (
          typeof name === 'string' && name.toLowerCase().includes(searchTerm)
        );
      });
    }

    if (ownerFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const owner = (entity as any).spec?.owner;
        const ownerStr =
          owner && typeof owner === 'string'
            ? owner
            : owner
            ? String(owner)
            : '';
        return ownerStr === ownerFilter;
      });
    }

    if (lifecycleFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const lifecycle = (entity as any).spec?.lifecycle;
        const lifecycleStr =
          lifecycle && typeof lifecycle === 'string'
            ? lifecycle
            : lifecycle
            ? String(lifecycle)
            : '';
        return lifecycleStr && lifecycleStr.toLowerCase() === lifecycleFilter;
      });
    }

    if (tagFilter !== 'all') {
      filtered = filtered.filter((entity: Entity) => {
        const tags = entity.metadata.tags || [];
        return tags.some(tag => tag.toLowerCase() === tagFilter);
      });
    }

    setEntities(filtered);
  }, [nameFilter, ownerFilter, lifecycleFilter, tagFilter, allEntities]);

  const renderOwner = (entity: Entity) => {
    const owner = (entity as any).spec?.owner;
    if (!owner || typeof owner !== 'string') {
      return '-';
    }

    const ownerRef = normalizeOwnerRef(owner);
    const group = ownerRef
      ? allEntities.find(entity => {
          return (
            entity.kind.toLowerCase() === 'group' &&
            normalizeOwnerRef(
              `${entity.metadata.namespace || 'default'}/${
                entity.metadata.name
              }`,
            ) === ownerRef
          );
        })
      : undefined;

    if (group) {
      return <Link to={getEntityRoute(group)}>{group.metadata.name}</Link>;
    }

    return owner;
  };

  const renderLinks = (entity: Entity) => {
    const links = entity.metadata.links || [];
    if (links.length === 0) {
      return '-';
    }

    return (
      <Box>
        {links.map((link, index) => (
          <Box key={index} mb={0.5}>
            <a href={link.url} target="_blank" rel="noopener noreferrer">
              Source Code
            </a>
          </Box>
        ))}
      </Box>
    );
  };

  const renderTags = (entity: Entity) => {
    const tags = entity.metadata.tags || [];
    if (tags.length === 0) {
      return '-';
    }
    return (
      <Box display="flex" flexWrap="wrap">
        {tags.map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            size="small"
            className={classes.tagChip}
          />
        ))}
      </Box>
    );
  };

  const columns: TableColumn<Entity>[] = [
    {
      title: 'Nombre',
      field: 'metadata.name',
      render: (entity: Entity) => (
        <Link to={getEntityRoute(entity)}>{entity.metadata.name}</Link>
      ),
    },
    {
      title: 'Descripción',
      field: 'metadata.description',
      render: (entity: Entity) => entity.metadata.description || '-',
    },
    {
      title: 'Owner',
      field: 'spec.owner',
      render: renderOwner,
    },
    {
      title: 'Tags',
      field: 'metadata.tags',
      render: renderTags,
    },
    {
      title: 'Enlaces',
      field: 'metadata.links',
      render: renderLinks,
    },
  ];

  return (
    <div className={classes.header}>
      <PageWithHeader title="Librerías" themeId="tool">
        <Content className={classes.content}>
          <Paper elevation={0} className={classes.filterPanel}>
            <Typography variant="h6" className={classes.filterTitle}>
              Filtros
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Nombre"
                  placeholder="Buscar por nombre..."
                  value={nameFilter}
                  onChange={e => setNameFilter(e.target.value)}
                  className={classes.filterControl}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Propietario</InputLabel>
                  <Select
                    value={ownerFilter}
                    onChange={e => setOwnerFilter(e.target.value as string)}
                    label="Propietario"
                  >
                    {availableOwners.map(owner => (
                      <MenuItem key={owner} value={owner}>
                        {owner === 'all' ? 'Todos' : owner}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Ciclo de Vida</InputLabel>
                  <Select
                    value={lifecycleFilter}
                    onChange={e => setLifecycleFilter(e.target.value as string)}
                    label="Ciclo de Vida"
                  >
                    {availableLifecycles.map(lifecycle => (
                      <MenuItem key={lifecycle} value={lifecycle}>
                        {formatKind(lifecycle)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.filterControl}
                >
                  <InputLabel>Tag</InputLabel>
                  <Select
                    value={tagFilter}
                    onChange={e => setTagFilter(e.target.value as string)}
                    label="Tag"
                  >
                    {availableTags.map(tag => (
                      <MenuItem key={tag} value={tag}>
                        {tag === 'all' ? 'Todos' : tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Box style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
            <Table
              title={`Librerías (${entities.length})`}
              options={{
                search: false,
                paging: true,
                pageSize: 20,
                padding: 'dense',
                tableLayout: 'auto',
              }}
              columns={columns}
              data={entities}
              isLoading={loading}
              emptyContent={
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" gutterBottom>
                    No se encontraron librerías
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {allEntities.length === 0
                      ? 'No hay librerías registradas en el catálogo'
                      : 'Intenta ajustar los filtros de búsqueda'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Content>
      </PageWithHeader>
    </div>
  );
};
